/**
 * 紹介リンク (yumesaku.tech の頁の URL に ?ref=<紹介コード>) で来た人の受け取り。
 *
 * - worker が ?ref= を見て cookie (yumesaku_ref) に置く (attachReferralCookie)
 * - 画面は cookie を localStorage の shegate_ref_code へ写す。同じ origin の /women の HP と LP は
 *   この値を読んで LINE の釦を LIFF へ向け、LIFF が LINE の利用者と紹介コードを結ぶ
 * - apps/client の中の LINE の入口 (/women/line/t/*) も、紹介コードがあれば LIFF へ向ける
 * - 買い切りの決済は、サーバー側が cookie の紹介コードを api-go へ渡す
 *
 * 置くのは紹介コードだけ。来た人の名前や住所などは持たない。
 * 紹介コードは画面に出すとき以外はログに出さない。
 */

/** cookie の名前。localStorage の名前は /women の HP と LP に合わせる */
export const REFERRAL_COOKIE = "yumesaku_ref";
export const REFERRAL_STORAGE_KEY = "shegate_ref_code";

/** 紹介の報酬の期間に合わせて 30 日。後から来た紹介コードが上書きする */
export const REFERRAL_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;

/**
 * 紹介コードの形。api-go が作るのは英大文字と数字の 8 字だが、
 * 受け口 (shegate-web の /api/referral/pending-link、LIFF の中継) と同じ 4〜10 字の英数字で受ける
 */
const REFERRAL_CODE_PATTERN = /^[A-Za-z0-9]{4,10}$/;

/** LINE 公式アカウントへの友だち追加を、紹介コードつきで通す LIFF (shegate-web の /liff/line-follow) */
const REFERRAL_LIFF_ID = "2008555780-gvTo7fwC";

export function isReferralCode(value: string | null | undefined): value is string {
  return typeof value === "string" && REFERRAL_CODE_PATTERN.test(value);
}

/** 住所の ?ref= が紹介コードの形なら返す */
export function referralCodeFromUrl(url: URL): string | null {
  const code = url.searchParams.get("ref");
  // api-go が作るコードは英大文字と数字。小文字で来ても数えられるよう、受け取った時点でそろえる
  return isReferralCode(code) ? code.toUpperCase() : null;
}

/** Cookie の見出しから紹介コードを読む。形の合わない値は無いものとして扱う */
export function referralCodeFromCookieHeader(header: string | null | undefined): string | null {
  if (!header) return null;
  for (const part of header.split(";")) {
    const eq = part.indexOf("=");
    if (eq === -1) continue;
    if (part.slice(0, eq).trim() !== REFERRAL_COOKIE) continue;
    const value = part.slice(eq + 1).trim();
    return isReferralCode(value) ? value.toUpperCase() : null;
  }
  return null;
}

/**
 * Secure を付けるか。手元 (localhost と 127.0.0.1 と ::1) だけ付けない。
 * 求めの protocol で決めないのは、本番の worker に届く求めが http に見える道があっても付け落とさないため
 */
export function referralCookieSecure(hostname: string): boolean {
  return !["localhost", "127.0.0.1", "[::1]", "::1"].includes(hostname);
}

/** Set-Cookie の値 (document.cookie に書く値も同じ) */
export function referralSetCookie(code: string, secure: boolean): string {
  return [
    `${REFERRAL_COOKIE}=${code}`,
    `Max-Age=${REFERRAL_COOKIE_MAX_AGE}`,
    "Path=/",
    "SameSite=Lax",
    ...(secure ? ["Secure"] : []),
  ].join("; ");
}

/**
 * ?ref= に紹介コードがあれば、応答に cookie を足して返す。無ければ応答をそのまま返す。
 * worker の出口で全部の応答 (301 や 302 も) に掛けるので、途中で早く返る道でも落ちない。
 * fetch や ASSETS の応答は見出しが書き換えられないので、作り直してから足す
 */
export function attachReferralCookie(request: Request, response: Response): Response {
  if (request.method !== "GET" && request.method !== "HEAD") return response;
  if (response.status === 101) return response;
  const url = new URL(request.url);
  const code = referralCodeFromUrl(url);
  if (!code) return response;
  const next = new Response(response.body, response);
  next.headers.append("set-cookie", referralSetCookie(code, referralCookieSecure(url.hostname)));
  // 人ごとに違う cookie を返すので、途中の写しに載せない
  next.headers.set("cache-control", "private, no-store");
  return next;
}

type FetchHandler<E, C> = { fetch(request: Request, env: E, ctx: C): Promise<Response> };

/** worker の入口を包み、どの道で返す応答にも attachReferralCookie を掛ける */
export function withReferralCookie<E, C>(inner: FetchHandler<E, C>): FetchHandler<E, C> {
  return {
    async fetch(request, env, ctx) {
      return attachReferralCookie(request, await inner.fetch(request, env, ctx));
    },
  };
}

/** 紹介コードつきで友だち追加へ進む LIFF の住所 */
export function referralLiffUrl(code: string): string {
  return `https://liff.line.me/${REFERRAL_LIFF_ID}?ref=${encodeURIComponent(code)}`;
}

/**
 * LINE の入口 (yumesaku.tech/women/line/t/*) を、紹介コードがあれば LIFF に替える。
 * /women/line/t/* は line.blueai.jp へ 302 で飛び、その時に query が落ちるので、ref を足しても届かない。
 * 診断の入口 (/women/line/job-diagnosis) は結果の受け渡しを持つので替えない
 */
export function lineEntryHref(href: string, code: string | null): string {
  if (!isReferralCode(code)) return href;
  let url: URL;
  try {
    url = new URL(href, "https://yumesaku.tech");
  } catch {
    return href;
  }
  if (url.hostname !== "yumesaku.tech") return href;
  if (!/^\/women\/line\/t\/[^/]+\/?$/.test(url.pathname)) return href;
  return referralLiffUrl(code);
}
